import React, { useState } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../services/firebaseConfig';
import { APPLICATION_STATUSES } from '../constants/crmConstants';

/**
 * ApplicationDetail Component
 * Full 10-section assessment view with clickable document links in Section 9.
 */
const ApplicationDetail = ({ application, users, settings, evaluationStages, onClose, onUpdate, currentUserId, viewOnly = false }) => {
  const [activeTab, setActiveTab] = useState('applicant');
  const [newNote, setNewNote] = useState('');
  
  // State for Rejection Workflow
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [additionalDocs, setAdditionalDocs] = useState(null);
  const [docDescription, setDocDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  // Inside ApplicationDetail component
  const [editingPreferenceIdx, setEditingPreferenceIdx] = useState(null);

  if (!application) return null;

  const handleAddNote = (e) => {
    e.preventDefault();
    if (viewOnly || !newNote.trim()) return;
    
    const currentUser = users.find(u => String(u.uid) === String(currentUserId) || String(u.id) === String(currentUserId));
    const currentAuthor = currentUser?.name || 'Portal User';
    
    const noteObj = {
      date: new Date().toISOString(),
      content: newNote.trim(),
      author: currentAuthor
    };
    
    onUpdate(application.id, {
      notes: [noteObj, ...(application.notes || [])]
    });
    setNewNote('');
  };

  const handleStageChange = (newStatus) => {
    if (viewOnly) return;
    
    if (newStatus === 'Rejected') {
      setShowRejectModal(true);
    } else {
      onUpdate(application.id, { 
        status: newStatus, 
        lastStatusChange: new Date().toISOString(),
        rejectionReason: null 
      });
    }
  };

  const submitRejection = async (e) => {
    e.preventDefault();
    if (!rejectionReason.trim()) return;

    // 1. Resolve current evaluator metadata for auditing
    const currentUser = users.find(u => String(u.uid) === String(currentUserId) || String(u.id) === String(currentUserId));
    const currentAuthor = currentUser?.name || 'Portal User';


    // 2. Format a structured timeline log to save under your notes grid
    const rejectionNoteObj = {
      date: new Date().toISOString(),
      content: `[System Notification: Application Rejected] Reason: ${rejectionReason.trim()}`,
      author: currentAuthor
    };

    // 3. Chain history array tracking notes configuration
    const updatedNotes = [rejectionNoteObj, ...(application.notes || [])];

    try {
      // 4. Update the Firestore Application document layout directly.
      // Changing the status to "Rejected" triggers your backend functions instantly!
      await onUpdate(application.id, { 
        status: 'Rejected',
        lastStatusChange: new Date().toISOString(),
        rejectionReason: rejectionReason.trim(), // Stored safely on root object
        notes: updatedNotes // Adds summary item seamlessly inside the notes panel
      });

      // 5. Reset Modal Context safely
      setShowRejectModal(false);
      setRejectionReason('');
    } catch (error) {
      console.error("Rejection persistence failure:", error);
      alert("System encountered an error saving rejection details. Please check connection.");
    }
  };

  const handleOutcomeChange = (prefIdx, outcome) => {
    if (viewOnly) return;
    const updatedSelections = [...(application.programSelections || [])];
    updatedSelections[prefIdx] = { ...updatedSelections[prefIdx], outcome };
    onUpdate(application.id, { programSelections: updatedSelections });
  };

  const getUniName = (id) => {
    if (!id) return 'Not Specified';
    const uni = (settings?.universities || []).find(u => String(u.id) === String(id));
    return uni ? uni.name : id;
  };


    const handleAdditionalUpload = async (e) => {
      e.preventDefault();
      if (!additionalDocs || isUploading) return;

      setIsUploading(true);
      try {
        const uploadPromises = Array.from(additionalDocs).map(async (file) => {
          const storageRef = ref(storage, `applications/${application.email}/evaluation_docs/${Date.now()}_${file.name}`);
          const snapshot = await uploadBytes(storageRef, file);
          const url = await getDownloadURL(snapshot.ref);
          return {
            url,
            name: file.name,
            description: docDescription.trim() || 'No description provided',
            uploadedAt: new Date().toISOString()
          };
        });

        const newDocObjects = await Promise.all(uploadPromises);
        const existingDocs = application.evaluationDocuments || [];
        
        // Update the application record with the new list of URLs
        await onUpdate(application.id, {
          evaluationDocuments: [...existingDocs, ...newDocObjects]
        });

        setAdditionalDocs(null);
        setDocDescription(''); // Reset description
        alert("Documents uploaded successfully!");
      } catch (error) {
        console.error("Upload failed:", error);
        alert("Failed to upload documents.");
      } finally {
        setIsUploading(false);
      }
    };

  const DetailBlock = ({ label, value, fullWidth }) => (
    <div className={`space-y-1 ${fullWidth ? 'col-span-full' : ''}`}>
      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{label}</p>
      <p className="text-sm font-bold text-slate-800 leading-tight">{value || 'N/A'}</p>
    </div>
  );

  const SectionHeader = ({ title, number }) => (
    <h4 className="text-lg font-black text-slate-900 tracking-tight uppercase border-l-4 border-[#0464AD] pl-4 mb-4">
      {number}. {title}
    </h4>
  );

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-500 pb-20">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button onClick={onClose} className="w-10 h-10 bg-white rounded-xl shadow-sm border border-slate-200 flex items-center justify-center text-slate-400 hover:text-[#0464AD] transition-all">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          </button>
          <div>
            <h2 className="text-2xl font-black text-slate-900 tracking-tighter uppercase leading-none">{application.applicantName}</h2>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Application ID: {application.id}</p>
          </div>
        </div>
        {viewOnly && (
          <div className="bg-amber-50 text-amber-600 px-4 py-2 rounded-xl border border-amber-100 text-[10px] font-black uppercase tracking-widest">
            Read Only
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-8 items-start">
        <div className="xl:col-span-3 space-y-6">
          <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden min-h-[700px] flex flex-col">
            <div className="flex px-8 bg-slate-50/50 border-b border-slate-100">
              {['applicant', 'application', 'assessment', 'notes', 'documents'].map((tab) => (
                <button key={tab} onClick={() => setActiveTab(tab)} className={`py-5 px-6 text-[10px] font-black uppercase tracking-widest border-b-2 transition-all ${activeTab === tab ? 'border-[#0464AD] text-[#0464AD]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>{tab}</button>
              ))}
            </div>

            <div className="p-10 flex-1 overflow-y-auto max-h-[80vh] space-y-12">
              {activeTab === 'applicant' && (
                <div className="space-y-10 animate-in fade-in duration-300">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div className="space-y-6">
                      <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest border-b pb-2">Personal Background</h4>
                      <div className="grid grid-cols-2 gap-6">
                        <DetailBlock label="First Name" value={application.firstName} />
                        <DetailBlock label="Last Name" value={application.lastName} />
                        <DetailBlock label="Email" value={application.email} />
                        <DetailBlock label="Phone" value={application.phone} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'application' && (
                <div className="animate-in fade-in duration-300 max-w-5xl space-y-12 pb-10">
                  <section>
                  <SectionHeader title="Personal Details" number={2} />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6 bg-slate-50/50 p-6 rounded-3xl border border-slate-100">
                      <DetailBlock label="DOB" value={application.dob} />
                      <DetailBlock label="Gender" value={application.gender} />
                      <DetailBlock label="Nationality" value={application.nationality} />
                      <DetailBlock label="Phone" value={application.phone} />
                      
                      {/* Address Fields */}
                      <DetailBlock label="Street Address" value={application.streetAddress} fullWidth />
                      <DetailBlock label="City" value={application.city} />
                      <DetailBlock label="State" value={application.state} />
                      <DetailBlock label="Postcode" value={application.postcode} />
                      <DetailBlock label="Country" value={application.country} />
                    </div>
                  </section>

                  <section>
                    <SectionHeader title="Program Selections" number={3} />
                    <div className="space-y-4">
                      {(application.programSelections || []).map((pref, i) => (
                        <div key={i} className="flex items-center gap-6 bg-slate-50/50 p-6 rounded-3xl border border-slate-100">
                          <span className="text-3xl font-black text-slate-200">#{i+1}</span>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 flex-1">
                            <DetailBlock label="Country" value={pref.country} />
                            <DetailBlock label="University" value={getUniName(pref.universityId)} />
                            <DetailBlock label="TWG Program" value={pref.twgProgram} />
                            <DetailBlock label="Intake" value={pref.intake} />
                            {/* ADD THIS LINE BELOW */}
                            <DetailBlock label="Destination Course" value={pref.course} fullWidth />
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section>
                    <SectionHeader title="Guardian Information" number={4} />
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-6 bg-slate-50/50 p-6 rounded-3xl border border-slate-100">
                      <DetailBlock label="Name" value={`${application.guardianGivenName} ${application.guardianFamilyName}`} />
                      <DetailBlock label="Relationship" value={application.guardianRelationship} />
                      <DetailBlock label="Occupation" value={application.guardianOccupation} />
                      <DetailBlock label="Guardian Email" value={application.guardianEmail} />
                      {/* ADDED: Guardian Phone Number */}
                      <DetailBlock label="Guardian Phone" value={application.guardianPhone} />
                      
                      {/* ADDED: Full Address Breakdown */}
                      {application.guardianAddressSame === 'No' ? (
                        <>
                          <DetailBlock label="Street Address" value={application.guardianStreetAddress} fullWidth />
                          <DetailBlock label="City" value={application.guardianCity} />
                          <DetailBlock label="State" value={application.guardianState} />
                          <DetailBlock label="Postcode" value={application.guardianPostcode} />
                          <DetailBlock label="Country" value={application.guardianCountry} />
                        </>
                      ) : (
                        <div className="col-span-full pt-2">
                          <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest bg-emerald-50 w-fit px-3 py-1 rounded-lg border border-emerald-100">
                            ✓ Same as Applicant Address
                          </p>
                        </div>
                      )}
                    </div>
                  </section>
                  

                  <section>
                    <SectionHeader title="Academic Qualifications" number={5} />
                    
                    {/* 1. Academic Qualifications List */}
                    <div className="space-y-4">
                      {(application.qualifications || []).map((qual, idx) => (
                        <div key={idx} className="bg-slate-50/50 p-6 rounded-3xl border border-slate-100">
                          <p className="text-[10px] font-black text-[#0464AD] mb-4">QUALIFICATION #{idx + 1}</p>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                            <DetailBlock label="Level" value={qual.highestQualification} />
                            <DetailBlock label="Institution" value={qual.institutionName} />
                            <DetailBlock label="Board/Uni" value={qual.educationBoard} />
                            <DetailBlock label="GPA / Marks" value={qual.marksGpa} />
                            <DetailBlock label="Language" value={qual.languageOfInstruction} />
                            <DetailBlock label="Status" value={qual.isCompleted} />
                            <DetailBlock label="Completion Date" value={qual.completionDate} />
                          </div>
                        </div>
                      ))}
                    </div> {/* This was the missing closing div tag */}

                    {/* 2. Academic Gap Info */}
                    {application.hasAcademicGap === 'Yes' && (
                      <div className="mt-6 p-6 bg-amber-50/50 rounded-3xl border border-amber-100">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <DetailBlock label="Academic Gap (>6 months)" value={application.hasAcademicGap} />
                          <DetailBlock label="Gap Reason" value={application.gapReason} />
                        </div>
                      </div>
                    )}

                    {/* Academic Documents Links - Robust Check */}
                    {application.academicDocumentsUrls && application.academicDocumentsUrls.length > 0 && (
                      <div className="mt-4 p-5 bg-white rounded-2xl border border-slate-100 shadow-sm">
                        <p className="text-[10px] font-black text-[#0464AD] uppercase tracking-widest mb-3">
                          Academic Documents / Transcripts
                        </p>
                        <div className="flex flex-wrap gap-3">
                          {application.academicDocumentsUrls.map((url, i) => (
                            <a 
                              key={i} 
                              href={url} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="text-[10px] font-bold text-[#0464AD] bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100 hover:bg-blue-100 transition-colors flex items-center gap-2"
                            >
                              <span>📄</span> View Transcript {i + 1}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </section>


                  {/* SECTION 6: ENGLISH PROFICIENCY */}
                  <section>
                    <SectionHeader title="English Proficiency" number={6} />
                    <div className="bg-slate-50/50 p-6 rounded-3xl border border-slate-100 space-y-6">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                        <DetailBlock label="Test Taken" value={application.hasEnglishTest} />
                        <DetailBlock label="Test Name" value={application.englishTestName} />
                        <DetailBlock label="Score" value={application.englishTestScore} />
                        <DetailBlock label="Verification ID" value={application.englishTestVerificationId} />
                        <DetailBlock label="Proficiency Notes" value={application.englishProficiencyDescription} fullWidth />
                      </div>

                      {/* ADDED: English Test Result Documents */}
                      {application.englishTestResultUrls && application.englishTestResultUrls.length > 0 && (
                        <div className="mt-4 p-5 bg-white rounded-2xl border border-slate-100 shadow-sm">
                          <p className="text-[10px] font-black text-[#0464AD] uppercase tracking-widest mb-3">
                            English Proficiency Evidence
                          </p>
                          <div className="flex flex-wrap gap-3">
                            {application.englishTestResultUrls.map((url, i) => (
                              <a 
                                key={i} 
                                href={url} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-[10px] font-bold text-[#0464AD] bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100 hover:bg-blue-100 transition-colors flex items-center gap-2"
                              >
                                <span>🔤</span> View English Result {i + 1}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </section>
                  
                  {/* SECTION 7: FINANCIAL ASSESSMENT */}
                  <section>
                    <SectionHeader title="Financial Assessment" number={7} />
                    <div className="bg-slate-50/50 p-6 rounded-3xl border border-slate-100 space-y-6">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                        <DetailBlock label="Primary Sponsor" value={application.primarySponsor} />
                        <DetailBlock label="Annual Income" value={application.sponsorAnnualIncome} />
                        <DetailBlock label="Liquid Funds" value={application.liquidFundsAmount} />
                        <DetailBlock label="Loan Planned" value={application.plannedLoanAmount} />
                        <DetailBlock label="Asset Value" value={application.assetValue} />
                      </div>

                      {/* ADDED: Other relevant funding details */}
                      {application.fundingAdditionalDetails && (
                        <div className="pt-4 border-t border-slate-200">
                          <DetailBlock 
                            label="Other Relevant Funding Details" 
                            value={application.fundingAdditionalDetails} 
                            fullWidth 
                          />
                        </div>
                      )}

                      {application.relevantFinancialDocsUrls && application.relevantFinancialDocsUrls.length > 0 && (
                        <div className="mt-4 p-5 bg-white rounded-2xl border border-slate-100 shadow-sm">
                          <p className="text-[10px] font-black text-[#0464AD] uppercase tracking-widest mb-3">
                            Supporting Financial Documents
                          </p>
                          <div className="flex flex-wrap gap-3">
                            {application.relevantFinancialDocsUrls.map((url, i) => (
                              <a 
                                key={i} 
                                href={url} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-[10px] font-bold text-[#0464AD] bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100 hover:bg-blue-100 transition-colors flex items-center gap-2"
                              >
                                <span>💰</span> View Financial Doc {i + 1}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* ADDED: Confirmation of Declaration */}
                      <div className="pt-4 flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-lg flex items-center justify-center text-[10px] font-black ${application.financialDeclaration ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-400'}`}>
                          {application.financialDeclaration ? '✓' : '✕'}
                        </div>
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                          Financial Capability Declaration {application.financialDeclaration ? 'Confirmed' : 'Not Confirmed'}
                        </span>
                      </div>
                    </div>
                  </section>
                  
                  <section>
                    <SectionHeader title="Immigration History" number={8} />
                    <div className="bg-slate-50/50 p-6 rounded-3xl border border-slate-100 space-y-6">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                        <DetailBlock label="Visa Refusal" value={application.visaRefusal} />
                        <DetailBlock label="Criminal History" value={application.criminalHistory} />
                        <DetailBlock label="Non-Compliant Record" value={application.nonCompliantRecord} />
                        <DetailBlock label="Overstay Record" value={application.overstayRecord} />
                        <DetailBlock label="Relatives in Target Country" value={application.relativesInCountry} />
                      </div>

                      {/* Display detailed explanation if any immigration issues were flagged */}
                      {(application.visaRefusal === 'Yes' || 
                        application.criminalHistory === 'Yes' || 
                        application.nonCompliantRecord === 'Yes' || 
                        application.overstayRecord === 'Yes') && (
                        <div className="pt-4 border-t border-slate-200">
                          <DetailBlock 
                            label="Detailed Immigration Explanation" 
                            value={application.immigrationDetails} 
                            fullWidth 
                          />
                        </div>
                      )}
                    </div>
                  </section>  


                  
                  {/* SECTION 9: UPLOADED DOCUMENTS */}
                  <section>
                    <SectionHeader title="Uploaded Documents" number={9} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Identification Documents */}
                      <div className="p-5 bg-slate-50/50 rounded-2xl border border-slate-100">
                        <p className="text-[10px] font-black text-[#0464AD] uppercase tracking-widest mb-2">Identification</p>
                        {/* FIX: Changed from section8PersonalDocsUrls to personalDocUrls */}
                        {application.personalDocUrls && application.personalDocUrls.length > 0 ? (
                          <div className="flex flex-col gap-2">
                            {application.personalDocUrls.map((url, i) => (
                              <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-[#0464AD] hover:underline flex items-center gap-2">
                                <span>📄 View Personal Doc {i + 1}</span>
                              </a>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs font-bold text-slate-400 italic">No identification files found.</p>
                        )}
                      </div>
                      
                      {/* Admission Documents */}
                      <div className="p-5 bg-slate-50/50 rounded-2xl border border-slate-100">
                        <p className="text-[10px] font-black text-[#0464AD] uppercase tracking-widest mb-2">Admission Related</p>
                        {/* FIX: Changed from section8AdmissionDocsUrls to admissionDocUrls */}
                        {application.admissionDocUrls && application.admissionDocUrls.length > 0 ? (
                          <div className="flex flex-col gap-2">
                            {application.admissionDocUrls.map((url, i) => (
                              <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-[#0464AD] hover:underline flex items-center gap-2">
                                <span>📄 View Admission Doc {i + 1}</span>
                              </a>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs font-bold text-slate-400 italic">No admission files found.</p>
                        )}
                      </div>
                    </div>
                  </section>

                  <section><SectionHeader title="Declarations & Consent" number={10} /><div className="bg-slate-900 p-8 rounded-[2rem] text-white space-y-4">{[{ l: 'Declaration Agreed', v: application.declarationAgree }, { l: 'Privacy Policy Accepted', v: application.privacyAgree }, { l: 'Partner Verification Done', v: application.partnerDeclarationAgree }, { l: '18+ Years Old', v: application.isAbove18 === 'Yes' }].map((d, i) => (<div key={i} className="flex items-center gap-3"><div className={`w-4 h-4 rounded flex items-center justify-center text-[8px] font-black ${d.v ? 'bg-emerald-500 text-white' : 'bg-white/10 text-white/20'}`}>✓</div><span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{d.l}</span></div>))}</div></section>
                </div>
              )}

              {activeTab === 'assessment' && (
                <div className="space-y-6 animate-in fade-in duration-300">
                  <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100">
                    <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest mb-6">Program Outcomes & Preferences</h4>
                    <table className="w-full text-left border-collapse">
                      <thead className="border-b border-slate-200">
                        <tr>
                          <th className="py-3 text-[9px] font-black text-slate-400 uppercase">Preference</th>
                          <th className="py-3 text-[9px] font-black text-slate-400 uppercase">Details</th>
                          <th className="py-3 text-[9px] font-black text-slate-400 uppercase text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {(application.programSelections || []).map((pref, idx) => (
                          <React.Fragment key={idx}>
                            <tr className={editingPreferenceIdx === idx ? 'bg-blue-50/50' : ''}>
                              <td className="py-4 text-xs font-black text-slate-400 align-top">P{idx + 1}</td>
                              <td className="py-4">
                                {editingPreferenceIdx === idx ? (
                                  /* RENDER FULL SECTION 3 FORM FOR EDITING */
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in slide-in-from-top-2">
                                    <div className="space-y-1">
                                      <label className="text-[8px] font-black text-slate-400 uppercase">Country</label>
                                      <select 
                                        className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs outline-none"
                                        value={pref.country}
                                        onChange={(e) => {
                                          const updated = [...application.programSelections];
                                          updated[idx] = { ...updated[idx], country: e.target.value, universityId: '', twgProgram: '', course: '', intake: '' };
                                          onUpdate(application.id, { programSelections: updated });
                                        }}
                                      >
                                        <option value="">Select Country...</option>
                                        {(settings?.countries || []).map(c => <option key={c} value={c}>{c}</option>)}
                                      </select>
                                    </div>
                                    
                                    <div className="space-y-1">
                                      <label className="text-[8px] font-black text-slate-400 uppercase">University</label>
                                      <select 
                                        className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs outline-none"
                                        value={pref.universityId}
                                        onChange={(e) => {
                                          const updated = [...application.programSelections];
                                          updated[idx] = { ...updated[idx], universityId: e.target.value, twgProgram: '', course: '', intake: '' };
                                          onUpdate(application.id, { programSelections: updated });
                                        }}
                                      >
                                        <option value="">Select University...</option>
                                        {(settings?.universities || []).filter(u => u.country === pref.country).map(u => (
                                          <option key={u.id} value={u.id}>{u.name}</option>
                                        ))}
                                      </select>
                                    </div>

                                    <div className="space-y-1">
                                      <label className="text-[8px] font-black text-slate-400 uppercase">TWG Program</label>
                                      <select 
                                        className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs outline-none"
                                        value={pref.twgProgram}
                                        onChange={(e) => {
                                          const updated = [...application.programSelections];
                                          updated[idx] = { ...updated[idx], twgProgram: e.target.value, intake: '' };
                                          onUpdate(application.id, { programSelections: updated });
                                        }}
                                      >
                                        <option value="">Select Program...</option>
                                        {((settings?.universities || []).find(u => u.id === pref.universityId)?.applicablePrograms || []).map(p => (
                                          <option key={p} value={p}>{p}</option>
                                        ))}
                                      </select>
                                    </div>

                                    <div className="space-y-1">
                                      <label className="text-[8px] font-black text-slate-400 uppercase">Intake</label>
                                      <select 
                                        className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs outline-none"
                                        value={pref.intake}
                                        onChange={(e) => {
                                          const updated = [...application.programSelections];
                                          updated[idx] = { ...updated[idx], intake: e.target.value };
                                          onUpdate(application.id, { programSelections: updated });
                                        }}
                                      >
                                        <option value="">Select Intake...</option>
                                        {(settings?.intakeMappings?.[pref.twgProgram] || []).map(date => (
                                          <option key={date} value={date}>{date}</option>
                                        ))}
                                      </select>
                                    </div>

                                    <div className="md:col-span-2 space-y-1">
                                      <label className="text-[8px] font-black text-slate-400 uppercase">Destination Course</label>
                                      <select 
                                        className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs outline-none"
                                        value={pref.course}
                                        onChange={(e) => {
                                          const updated = [...application.programSelections];
                                          updated[idx] = { ...updated[idx], course: e.target.value };
                                          onUpdate(application.id, { programSelections: updated });
                                        }}
                                      >
                                        <option value="">Select Course...</option>
                                        {((settings?.universities || []).find(u => u.id === pref.universityId)?.courses?.[pref.twgProgram] || []).map(c => (
                                          <option key={c} value={c}>{c}</option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                ) : (
                                  /* RENDER READ-ONLY VIEW */
                                  <>
                                    <p className="text-sm font-bold text-slate-800">{getUniName(pref.universityId)}</p>
                                    <p className="text-[10px] font-medium text-slate-500">{pref.twgProgram} - {pref.course}</p>
                                    <p className="text-[9px] font-black text-slate-400 uppercase mt-0.5">{pref.intake}</p>
                                  </>
                                )}
                              </td>
                              <td className="py-4 text-right align-top">
                                <div className="flex flex-col items-end gap-2">
                                  <div className="flex gap-2">
                                    {!viewOnly && (
                                      <button 
                                        onClick={() => setEditingPreferenceIdx(editingPreferenceIdx === idx ? null : idx)}
                                        className={`p-2 rounded-lg transition-colors ${editingPreferenceIdx === idx ? 'bg-[#0464AD] text-white' : 'bg-white border border-slate-200 text-slate-400 hover:text-[#0464AD]'}`}
                                        title="Edit Preference"
                                      >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                        </svg>
                                      </button>
                                    )}
                                    <select 
                                      disabled={viewOnly}
                                      value={pref.outcome || 'Pending'} 
                                      onChange={(e) => handleOutcomeChange(idx, e.target.value)} 
                                      className={`text-[10px] font-black uppercase px-3 py-1.5 rounded-lg border ${viewOnly ? 'cursor-not-allowed bg-slate-50' : ''}`}
                                    >
                                      <option value="Pending">Pending</option>
                                      <option value="Accepted">Accepted</option>
                                      <option value="Rejected">Rejected</option>
                                    </select>
                                  </div>
                                  {editingPreferenceIdx === idx && (
                                    <button 
                                      onClick={() => setEditingPreferenceIdx(null)}
                                      className="text-[9px] font-black text-emerald-600 uppercase tracking-widest bg-emerald-50 px-3 py-1 rounded-md border border-emerald-100"
                                    >
                                      ✓ Done Editing
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'notes' && (
                <div className="space-y-8 animate-in fade-in duration-300">
                  {!viewOnly && (
                    <form onSubmit={handleAddNote} className="relative">
                      <textarea className="w-full bg-slate-50 border border-slate-200 rounded-3xl p-6 text-sm outline-none min-h-[120px]" placeholder="Add evaluation note..." value={newNote} onChange={(e) => setNewNote(e.target.value)} />
                      <button type="submit" className="absolute bottom-4 right-4 bg-[#0464AD] text-white px-5 py-2 rounded-xl text-[10px] font-black uppercase">Post Note</button>
                    </form>
                  )}
                  <div className="bg-white border border-slate-100 rounded-3xl overflow-hidden shadow-sm">
                    <table className="w-full text-left border-collapse">
                      <thead className="bg-slate-50/50 border-b border-slate-100">
                        <tr><th className="px-6 py-4 text-[9px] font-black text-slate-400 uppercase">Date</th><th className="px-6 py-4 text-[9px] font-black text-slate-400 uppercase">Author</th><th className="px-6 py-4 text-[9px] font-black text-slate-400 uppercase">Comment</th></tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {(application.notes || []).map((note, i) => (
                          <tr key={i} className="hover:bg-slate-50/20 transition-colors">
                            <td className="px-6 py-4 text-[10px] font-medium text-slate-400">{new Date(note.date).toLocaleDateString()}</td>
                            <td className="px-6 py-4"><span className="text-[10px] font-black text-[#0464AD] uppercase">{note.author}</span></td>
                            <td className="px-6 py-4 text-xs font-bold text-slate-700 leading-relaxed">{note.content}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}


              {activeTab === 'documents' && (
                <div className="animate-in fade-in duration-300 space-y-10">
                  {!viewOnly && (
                    <div className="bg-slate-50 p-8 rounded-[2rem] border border-slate-100">
                      <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest mb-4">Upload Evaluation Documents</h4>
                      <form onSubmit={handleAdditionalUpload} className="space-y-4">
                        {/* Description Field */}
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Document Description</label>
                          <input 
                            type="text" 
                            placeholder="e.g. Revised Offer Letter, Financial Verification..."
                            className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]"
                            value={docDescription}
                            onChange={e => setDocDescription(e.target.value)}
                          />
                        </div>

                        {/* File Input */}
                        <div className="relative group">
                          <input type="file" className="hidden" id="eval-docs-upload" multiple onChange={e => setAdditionalDocs(e.target.files)} />
                          <label htmlFor="eval-docs-upload" className="flex items-center justify-between w-full bg-white border border-slate-200 rounded-2xl p-6 text-sm cursor-pointer hover:border-[#0464AD] transition-all">
                            <span className="text-slate-500 truncate">
                              {additionalDocs ? `${additionalDocs.length} files selected` : 'Select files...'}
                            </span>
                            <svg className="w-6 h-6 text-slate-300 group-hover:text-[#0464AD]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>
                          </label>
                        </div>

                        <button 
                          type="submit" 
                          disabled={!additionalDocs || isUploading}
                          className="bg-[#0464AD] text-white px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg disabled:opacity-50 transition-all active:scale-95"
                        >
                          {isUploading ? 'Uploading...' : 'Confirm & Upload'}
                        </button>
                      </form>
                    </div>
                  )}

                  {/* Display List with Descriptions */}
                  <div className="space-y-4">
                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest ml-2">Internal Evaluation Documents</h4>
                    {application.evaluationDocuments && application.evaluationDocuments.length > 0 ? (
                      <div className="grid grid-cols-1 gap-4">
                        {application.evaluationDocuments.map((doc, i) => (
                          <a 
                            key={i} href={doc.url} target="_blank" rel="noopener noreferrer" 
                            className="flex items-center justify-between p-5 bg-white border border-slate-100 rounded-2xl shadow-sm hover:border-[#0464AD] transition-all group"
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-500 text-xl">📄</div>
                              <div>
                                <p className="text-sm font-bold text-slate-800">{doc.description}</p>
                                <p className="text-[10px] text-slate-400 font-medium uppercase tracking-tight mt-0.5">Filename: {doc.name}</p>
                              </div>
                            </div>
                            <div className="text-[9px] font-black text-[#0464AD] uppercase bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100 group-hover:bg-[#0464AD] group-hover:text-white transition-colors">
                              View Document
                            </div>
                          </a>
                        ))}
                      </div>
                    ) : (
                      <div className="py-20 text-center bg-slate-50/50 rounded-[2rem] border border-dashed border-slate-200">
                        <p className="text-sm text-slate-400 font-medium italic">No internal documents found.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}               

            </div>
          </div>
        </div>

        {/* Management Sidebar */}
        <div className="xl:col-span-1 space-y-6 sticky top-24 h-fit">
          <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm">
            <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-8 leading-none">Application Details</h3>
            <div className="space-y-8">
              <div className="space-y-3">
                <label className="text-[9px] font-black text-[#0464AD] uppercase tracking-widest block">Application Stage</label>
                <select 
                  disabled={viewOnly}
                  value={application.status} 
                  onChange={(e) => handleStageChange(e.target.value)} 
                  className={`w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-xs font-black uppercase text-slate-800 ${viewOnly ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  {APPLICATION_STATUSES.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              {/* PLAIN TEXT REJECTION REASON DISPLAY */}
              {application.status === 'Rejected' && application.rejectionReason && (
                <div className="pt-2 animate-in slide-in-from-top-2">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Reason for Rejection</p>
                  <p className="text-sm font-bold text-slate-800 leading-tight">{application.rejectionReason}</p>
                </div>
              )}



              <div className="grid grid-cols-1 gap-6 pt-6 border-t border-slate-100">
                <div>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Associated Agent</p>
                  <p className="text-xs font-bold mt-1 text-slate-800 uppercase leading-tight">
                    {application.partnerName || 'Direct Application'}
                  </p>
                  {(() => {
                    const agentData = (settings?.sourceAgents || []).find(
                      a => (typeof a === 'object' && a.name === application.partnerName)
                    );
                    return agentData?.code ? (
                      <p className="text-[9px] font-black text-[#0464AD] uppercase tracking-wider mt-0.5">
                        Code: {agentData.code}
                      </p>
                    ) : null;
                  })()}
                </div>

                {/* 2. Agent Email ID */}
                <div>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Agent Contact</p>
                  <p className="text-xs font-bold mt-1 text-slate-600 truncate">
                    {(() => {
                      const agentData = (settings?.sourceAgents || []).find(
                        a => (typeof a === 'object' && a.name === application.partnerName)
                      );
                      return agentData?.email || 'No email registered';
                    })()}
                  </p>
                </div>

                <div>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Submitted On</p>
                  <p className="text-xs font-bold mt-1.5 text-slate-800">
                    {(() => {
                      const rawDate = application.dateApplied || application.createdAt || application.updatedAt;
                      if (!rawDate) return 'Pending Submission';
                      const dateObj = rawDate.seconds ? new Date(rawDate.seconds * 1000) : new Date(rawDate);
                      return isNaN(dateObj.getTime()) ? 'Invalid Date' : dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                    })()}
                  </p>
                </div>

                <div>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">App Owner</p>
                  <select 
                    disabled={viewOnly}
                    value={application.assignedTo || ''} 
                    onChange={(e) => onUpdate(application.id, { assignedTo: e.target.value })} 
                    className={`w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-[10px] font-black uppercase text-slate-800 outline-none ${viewOnly ? 'cursor-not-allowed opacity-50' : ''}`}
                  >
                    <option value="">Unassigned</option>
                    {(users || [])
                      .filter(u => u.role === 'TWG Apps and Visa')
                      .map(u => <option key={u.uid} value={u.uid}>{u.name}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* REJECTION REASON POPUP */}
      {showRejectModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-md rounded-[2.5rem] shadow-2xl p-8 animate-in zoom-in-95 duration-200">
            <h3 className="text-2xl font-black text-slate-900 tracking-tighter uppercase mb-2">Rejection Details</h3>
            <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mb-6">Please provide a reason for rejecting this application</p>
            
            <form onSubmit={submitRejection} className="space-y-6">
              <textarea 
                required
                className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD] min-h-[120px] resize-none font-bold text-slate-800"
                placeholder="Type reason here..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
              <div className="flex gap-4">
                <button 
                  type="button" 
                  onClick={() => setShowRejectModal(false)}
                  className="flex-1 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-800"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="flex-1 bg-slate-900 text-white py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl hover:bg-[#0464AD] transition-all active:scale-95"
                >
                  Confirm Rejection
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationDetail;